'use client';

import { c as _c } from "react/compiler-runtime";
import { ChartTooltipPanel, FloatingTooltip } from '@/domains/pages/shared/chart-tooltip';
import { PowerBarMarker } from '@/domains/pages/shared/power-bar-marker';
import { PowerBarDotMarker } from '@/domains/pages/shared/power-bar-dot-marker';
import { AIFI_NEGATIVE_COLOR, AIFI_POSITIVE_TEXT_COLOR, BENCHMARK_MEDIAN_LABEL, BENCHMARK_TOP_QUARTILE_LABEL } from '@/domains/pages/ai-proficiency/constants';
import { POWER_BAR_COMPARISON_MARKER_COLOR, POWER_BAR_COMPARISON_MARKER_COLOR_LIT, POWER_BAR_DOT_GAP_PX, POWER_BAR_MARKER_LINE_WIDTH, POWER_BAR_TRACK_CLASS, powerBarMarkerPosition, powerBarScoreFillStyle, selectedValueTone, STANDARD_POWER_BAR_LAYOUT } from '@/domains/pages/shared/power-bar-shared';
import { AIFI_DATA_NUMBER_CLASS } from '@/domains/pages/ai-proficiency/typography';
import { cn } from '@/utils/shad';
import { formatScore } from '@/utils/format.util';
import { useCallback, useRef, useState } from 'react';
type PowerBarHoverTarget = 'bar' | 'median' | 'topQuartile';
type PowerBarProps = {
  score: number | null;
  median?: number;
  topQuartile?: number;
  className?: string;
};
const TOP_QUARTILE_DOT_GAP_PX = POWER_BAR_DOT_GAP_PX;
function orgValueTone(isAhead: boolean) {
  return selectedValueTone(isAhead);
}
export function PowerBar(t0) {
  const $ = _c(73);
  const {
    score,
    median,
    topQuartile,
    className
  } = t0;
  const barRef = useRef(null);
  const [hoverTarget, setHoverTarget] = useState(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      x: 0,
      y: 0
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [tooltipPos, setTooltipPos] = useState(t1);
  const orgScore = score ?? 0;
  let t2;
  if ($[1] !== orgScore) {
    t2 = powerBarMarkerPosition(orgScore);
    $[1] = orgScore;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const scoreFill = t2;
  const hasBenchmarks = median != null && topQuartile != null;
  const vsMedian = hasBenchmarks ? orgScore - median : 0;
  const vsTopQuartile = hasBenchmarks ? orgScore - topQuartile : 0;
  const aheadOfMedian = vsMedian >= 0;
  const aheadOfTopQuartile = vsTopQuartile >= 0;
  const barTopPx = STANDARD_POWER_BAR_LAYOUT.trackTopPx;
  const dotTopPx = barTopPx + STANDARD_POWER_BAR_LAYOUT.trackHeightPx + TOP_QUARTILE_DOT_GAP_PX;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = ratio => {
      const bar = barRef.current;
      if (!bar) {
        return;
      }
      const rect = bar.getBoundingClientRect();
      setTooltipPos({
        x: rect.left + rect.width * Math.min(1, Math.max(0, ratio)),
        y: rect.top
      });
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const updateTooltipAtRatio = t3;
  let t4;
  if ($[4] !== orgScore) {
    t4 = () => {
      updateTooltipAtRatio(orgScore / 100);
      setHoverTarget("bar");
    };
    $[4] = orgScore;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const showBarTooltip = t4;
  let t5;
  if ($[6] !== hasBenchmarks || $[7] !== median) {
    t5 = () => {
      if (!hasBenchmarks) {
        return;
      }
      updateTooltipAtRatio(median / 100);
      setHoverTarget("median");
    };
    $[6] = hasBenchmarks;
    $[7] = median;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const showMedianTooltip = t5;
  let t6;
  if ($[9] !== hasBenchmarks || $[10] !== topQuartile) {
    t6 = () => {
      if (!hasBenchmarks) {
        return;
      }
      updateTooltipAtRatio(topQuartile / 100);
      setHoverTarget("topQuartile");
    };
    $[9] = hasBenchmarks;
    $[10] = topQuartile;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const showTopQuartileTooltip = t6;
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => setHoverTarget(null);
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const hideTooltip = t7;
  const barLit = hoverTarget === "bar";
  const medianLit = hoverTarget === "median";
  const topQuartileLit = hoverTarget === "topQuartile";
  let t8;
  if ($[13] !== aheadOfMedian || $[14] !== aheadOfTopQuartile || $[15] !== hasBenchmarks || $[16] !== hoverTarget || $[17] !== median || $[18] !== orgScore || $[19] !== topQuartile || $[20] !== vsMedian || $[21] !== vsTopQuartile) {
    t8 = hoverTarget === "bar" ? hasBenchmarks ? [{
      label: "Organization",
      value: formatScore(orgScore, {
        decimals: 1
      }),
      valueClassName: "text-foreground"
    }, {
      label: BENCHMARK_MEDIAN_LABEL,
      value: formatScore(median, {
        decimals: 1
      })
    }, {
      label: BENCHMARK_TOP_QUARTILE_LABEL,
      value: formatScore(topQuartile, {
        decimals: 1
      })
    }, {
      label: "vs. median",
      value: `${aheadOfMedian ? "+" : ""}${formatScore(vsMedian, {
        decimals: 1
      })}`,
      valueStyle: {
        color: aheadOfMedian ? AIFI_POSITIVE_TEXT_COLOR : AIFI_NEGATIVE_COLOR
      }
    }, {
      label: "vs. quartile",
      value: `${aheadOfTopQuartile ? "+" : ""}${formatScore(vsTopQuartile, {
        decimals: 1
      })}`,
      valueStyle: {
        color: aheadOfTopQuartile ? AIFI_POSITIVE_TEXT_COLOR : AIFI_NEGATIVE_COLOR
      }
    }] : [{
      label: "Organization",
      value: formatScore(orgScore, {
        decimals: 1
      }),
      valueClassName: "text-foreground"
    }] : hoverTarget === "median" ? [{
      label: BENCHMARK_MEDIAN_LABEL,
      value: formatScore(median, {
        decimals: 1
      })
    }, {
      label: "Organization",
      value: formatScore(orgScore, {
        decimals: 1
      }),
      ...orgValueTone(aheadOfMedian)
    }, {
      label: "Difference",
      value: `${aheadOfMedian ? "+" : ""}${formatScore(vsMedian, {
        decimals: 1
      })}`,
      ...orgValueTone(aheadOfMedian)
    }] : hoverTarget === "topQuartile" ? [{
      label: BENCHMARK_TOP_QUARTILE_LABEL,
      value: formatScore(topQuartile, {
        decimals: 1
      })
    }, {
      label: "Organization",
      value: formatScore(orgScore, {
        decimals: 1
      }),
      ...orgValueTone(aheadOfTopQuartile)
    }, {
      label: "Difference",
      value: `${aheadOfTopQuartile ? "+" : ""}${formatScore(vsTopQuartile, {
        decimals: 1
      })}`,
      ...orgValueTone(aheadOfTopQuartile)
    }] : [];
    $[13] = aheadOfMedian;
    $[14] = aheadOfTopQuartile;
    $[15] = hasBenchmarks;
    $[16] = hoverTarget;
    $[17] = median;
    $[18] = orgScore;
    $[19] = topQuartile;
    $[20] = vsMedian;
    $[21] = vsTopQuartile;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  const tooltipRows = t8;
  let t9;
  if ($[23] !== className) {
    t9 = cn("relative", className);
    $[23] = className;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  const t10 = barLit && "brightness-110";
  let t11;
  if ($[25] !== t10) {
    t11 = cn("absolute right-0 left-0 cursor-pointer transition-[filter] duration-150", t10);
    $[25] = t10;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      top: barTopPx,
      height: `${STANDARD_POWER_BAR_LAYOUT.trackHeightPx}px`
    };
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  const t13 = barLit && "brightness-105";
  let t14;
  if ($[28] !== t13) {
    t14 = cn("absolute inset-0 transition-[filter] duration-150", POWER_BAR_TRACK_CLASS, t13);
    $[28] = t13;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== t14) {
    t15 = <div className={t14} />;
    $[30] = t14;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  const t16 = barLit && "brightness-105";
  let t17;
  if ($[32] !== t16) {
    t17 = cn("absolute inset-y-0 left-0 transition-[filter] duration-150", t16);
    $[32] = t16;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  let t18;
  if ($[34] !== orgScore) {
    t18 = powerBarScoreFillStyle(orgScore);
    $[34] = orgScore;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  let t19;
  if ($[36] !== scoreFill || $[37] !== t18) {
    t19 = {
      width: scoreFill,
      ...t18
    };
    $[36] = scoreFill;
    $[37] = t18;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  let t20;
  if ($[39] !== t17 || $[40] !== t19) {
    t20 = <div className={t17} style={t19} />;
    $[39] = t17;
    $[40] = t19;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  let t21;
  if ($[42] !== showBarTooltip || $[43] !== t11 || $[44] !== t15 || $[45] !== t20) {
    t21 = <div className={t11} style={t12} onMouseEnter={showBarTooltip}>{t15}{t20}</div>;
    $[42] = showBarTooltip;
    $[43] = t11;
    $[44] = t15;
    $[45] = t20;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  let t22;
  if ($[47] !== hasBenchmarks || $[48] !== median || $[49] !== medianLit || $[50] !== showMedianTooltip || $[51] !== showTopQuartileTooltip || $[52] !== topQuartile || $[53] !== topQuartileLit) {
    t22 = hasBenchmarks ? <><PowerBarMarker layout={STANDARD_POWER_BAR_LAYOUT} position={powerBarMarkerPosition(median)} lit={medianLit} color={POWER_BAR_COMPARISON_MARKER_COLOR} litColor={POWER_BAR_COMPARISON_MARKER_COLOR_LIT} ariaLabel="Benchmark median marker" onMouseEnter={showMedianTooltip} onBlur={hideTooltip} /><PowerBarDotMarker layout={STANDARD_POWER_BAR_LAYOUT} position={powerBarMarkerPosition(topQuartile)} lit={topQuartileLit} color={POWER_BAR_COMPARISON_MARKER_COLOR} litColor={POWER_BAR_COMPARISON_MARKER_COLOR_LIT} ariaLabel="Top quartile marker" onMouseEnter={showTopQuartileTooltip} onFocus={showTopQuartileTooltip} onBlur={hideTooltip} /><div style={{
        height: dotTopPx + 4
      }} /></> : <div style={{
      height: STANDARD_POWER_BAR_LAYOUT.trackHeightPx + 4
    }} />;
    $[47] = hasBenchmarks;
    $[48] = median;
    $[49] = medianLit;
    $[50] = showMedianTooltip;
    $[51] = showTopQuartileTooltip;
    $[52] = topQuartile;
    $[53] = topQuartileLit;
    $[54] = t22;
  } else {
    t22 = $[54];
  }
  let t23;
  if ($[55] !== t21 || $[56] !== t22) {
    t23 = <div ref={barRef} className="relative" onMouseLeave={hideTooltip}>{t21}{t22}</div>;
    $[55] = t21;
    $[56] = t22;
    $[57] = t23;
  } else {
    t23 = $[57];
  }
  let t24;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <div className={cn("flex justify-between text-[10px] leading-none text-muted-foreground", AIFI_DATA_NUMBER_CLASS)}><span>0</span><span>100</span></div>;
    $[58] = t24;
  } else {
    t24 = $[58];
  }
  let t25;
  if ($[59] !== hasBenchmarks) {
    t25 = hasBenchmarks ? <div className="mt-2.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground"><span className="flex items-center gap-2"><span className="h-3 shrink-0 rounded-full" style={{
          width: POWER_BAR_MARKER_LINE_WIDTH,
          backgroundColor: POWER_BAR_COMPARISON_MARKER_COLOR
        }} />{BENCHMARK_MEDIAN_LABEL}</span><span className="flex items-center gap-2"><span className="size-1.5 shrink-0 rounded-full" style={{
          backgroundColor: POWER_BAR_COMPARISON_MARKER_COLOR
        }} />{BENCHMARK_TOP_QUARTILE_LABEL}</span></div> : null;
    $[59] = hasBenchmarks;
    $[60] = t25;
  } else {
    t25 = $[60];
  }
  const t26 = hoverTarget != null;
  let t27;
  if ($[61] !== tooltipRows) {
    t27 = <ChartTooltipPanel rows={tooltipRows} />;
    $[61] = tooltipRows;
    $[62] = t27;
  } else {
    t27 = $[62];
  }
  let t28;
  if ($[63] !== t26 || $[64] !== t27 || $[65] !== tooltipPos.x || $[66] !== tooltipPos.y) {
    t28 = <FloatingTooltip open={t26} x={tooltipPos.x} y={tooltipPos.y}>{t27}</FloatingTooltip>;
    $[63] = t26;
    $[64] = t27;
    $[65] = tooltipPos.x;
    $[66] = tooltipPos.y;
    $[67] = t28;
  } else {
    t28 = $[67];
  }
  let t29;
  if ($[68] !== t23 || $[69] !== t25 || $[70] !== t28 || $[71] !== t9) {
    t29 = <div className={t9}>{t23}{t24}{t25}{t28}</div>;
    $[68] = t23;
    $[69] = t25;
    $[70] = t28;
    $[71] = t9;
    $[72] = t29;
  } else {
    t29 = $[72];
  }
  return t29;
}